import { useQuery } from "@tanstack/react-query";

import {
  fetchMyProfile,
  listMeetingRewards,
  type Deal,
  type MeetingRewardClaim,
  type Profile,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Badge } from "@/components/ui/badge";
import {
  formatAudCents,
  SPIF_ALLOCATION_RULE,
  SPIF_FLAT_RATE_CENTS,
  spifPayoutType,
} from "@/lib/spif";

type BadgeVariant = "secondary" | "success" | "destructive" | "outline";

interface SpifLineState {
  badge: string;
  badgeVariant: BadgeVariant;
  detail: string | null;
}

function lineState(deal: Deal): SpifLineState {
  if (deal.spif_eligible === false) {
    return { badge: "Not eligible", badgeVariant: "outline", detail: null };
  }
  if (deal.spif_eligible === true && deal.spif_paid) {
    return {
      badge: "✓ Paid",
      badgeVariant: "success",
      detail: deal.spif_paid_at
        ? `Processed: ${new Date(deal.spif_paid_at).toLocaleDateString()}`
        : null,
    };
  }
  if (deal.spif_eligible === true) {
    return { badge: "Pending payment", badgeVariant: "secondary", detail: null };
  }
  // closed_won but not yet adjudicated by RM/RA.
  return { badge: "Eligibility under review", badgeVariant: "outline", detail: null };
}

/** DD/MM/YYYY (Australian). */
function formatAuDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/** One row in the closed-won "Rewards earned" list (CHP-391). */
interface EarnedReward {
  key: string;
  name: string;
  amountCents: number;
  approvedAt: string | null;
}

/**
 * "Associated Sales Incentives" / "Rewards earned" — the SPIF tracker in the
 * BDM/PO deal detail drawer/panel.
 *
 * For a closed-won deal the section is titled "Rewards earned" (CHP-391) and
 * lists ONLY rewards that an RA/RM has approved: the Deal Registration Bonus
 * (when adjudicated eligible — approval date `spif_adjudicated_at`) plus every
 * APPROVED meeting-reward claim (GET /deals/{id}/meeting-reward, filtered to
 * status==='approved' — approval date `decided_at`). Each row reads
 * "<name> — <$value> — <Date approved>".
 *
 * For earlier statuses the legacy single-line SPIF state is retained — the
 * deal hasn't been adjudicated, so there's nothing "earned" to list yet.
 *
 * The allocation-rule line comes from the firm's payout setup (spif_opt_in +
 * spif_payout_type, org-resolved onto the profile since CHP-1175 — BDMs see
 * it too). When no rule resolves (org-less, or the PO hasn't elected) the
 * line is simply omitted.
 */
export function DealSpifTracker({ deal }: { deal: Deal }) {
  const { accessToken } = useCurrentUser();

  const profile = useQuery<Profile | null>({
    // Same key as the settings page — shares its cache entry.
    queryKey: ["profiles", "me"],
    queryFn: () => fetchMyProfile(accessToken!),
    enabled: !!accessToken,
  });

  const isClosedWon = deal.deal_status === "closed_won";

  // Approved meeting rewards back the closed-won "Rewards earned" list.
  const rewards = useQuery<MeetingRewardClaim[]>({
    queryKey: ["deals", "meeting-rewards", deal.id],
    queryFn: () => listMeetingRewards(accessToken!, deal.id),
    enabled: !!accessToken && isClosedWon,
  });

  // SPIF state only exists once a deal reaches closed-won (or has been
  // adjudicated); earlier statuses have nothing truthful to show.
  if (deal.deal_status !== "closed_won" && deal.spif_eligible === null) return null;

  const payout = spifPayoutType(profile.data);
  const allocationRule = payout ? SPIF_ALLOCATION_RULE[payout] : null;

  // CHP-391: closed-won → "Rewards earned", approved rewards only.
  if (isClosedWon) {
    const earned: EarnedReward[] = [];
    // The Deal Registration Bonus is "earned" once adjudicated eligible.
    if (deal.spif_eligible === true) {
      earned.push({
        key: "spif-bonus",
        name: "Deal Registration Bonus",
        amountCents: SPIF_FLAT_RATE_CENTS,
        approvedAt: deal.spif_adjudicated_at,
      });
    }
    // Approved meeting rewards only.
    for (const claim of rewards.data ?? []) {
      if (claim.status !== "approved") continue;
      earned.push({
        key: `meeting-${claim.id}`,
        name: "Meeting reward",
        amountCents: claim.amount_cents,
        approvedAt: claim.decided_at,
      });
    }

    return (
      <section data-testid="deal-spif-tracker">
        <h2 className="mb-3 text-base font-semibold">Rewards earned</h2>
        {rewards.isError ? (
          // CHP-804: a failed meeting-rewards fetch must NOT read as "no
          // approved rewards" — the list would silently drop approved rewards.
          <div role="alert" className="text-sm text-destructive">
            <p>Couldn&apos;t load approved rewards. Please try again.</p>
            <button
              type="button"
              onClick={() => void rewards.refetch()}
              className="mt-1 text-sm font-medium underline underline-offset-2"
            >
              Retry
            </button>
          </div>
        ) : earned.length === 0 ? (
          <p className="text-sm text-muted-foreground">No approved rewards yet.</p>
        ) : (
          <ul className="space-y-2">
            {earned.map((r) => (
              <li
                key={r.key}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3"
              >
                <p className="text-sm font-medium">{r.name}</p>
                <p className="text-sm text-muted-foreground">
                  {formatAudCents(r.amountCents)} — {formatAuDate(r.approvedAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
        {allocationRule ? (
          <p className="mt-2 text-xs text-muted-foreground">{allocationRule}</p>
        ) : null}
      </section>
    );
  }

  // Pre-closed-won (adjudicated but not closed-won) — legacy single-line state.
  const line = lineState(deal);
  return (
    <section data-testid="deal-spif-tracker">
      <h2 className="mb-3 text-base font-semibold">Associated Sales Incentives</h2>
      <ul className="space-y-2">
        <li className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-3">
          <div>
            <p className="text-sm font-medium">Deal Registration Bonus</p>
            <p className="text-sm text-muted-foreground">
              {formatAudCents(SPIF_FLAT_RATE_CENTS)} AUD
              {line.detail ? ` (${line.detail})` : ""}
            </p>
          </div>
          <Badge variant={line.badgeVariant}>{line.badge}</Badge>
        </li>
      </ul>
      {allocationRule ? (
        <p className="mt-2 text-xs text-muted-foreground">{allocationRule}</p>
      ) : null}
    </section>
  );
}
